import { ArrowLeft, ArrowRight } from 'lucide-react'
import { observer } from 'mobx-react-lite'
import { useState, type FormEvent } from 'react'
import { Link } from 'react-router'
import { toApiError } from '@/api/errors'
import { Button } from '@/components/ui/Button'
import { Field } from '@/components/ui/Field'
import { InlineAlert } from '@/components/ui/InlineAlert'
import { Input } from '@/components/ui/Input'
import { useT } from '@/i18n'
import { cn } from '@/lib/cn'
import { pickName } from '@/lib/format'
import { useStores } from '@/stores'
import { AuthLayout } from './AuthLayout'
import { PasswordInput } from './PasswordInput'

/**
 * The second step when the credentials match in several companies: the
 * last-used one (is_current) is preselected; a company whose user was blocked
 * or changed its password since is listed but disabled.
 */
const CompanyStep = observer(function CompanyStep() {
  const { auth, ui } = useStores()
  const t = useT()
  const companies = auth.pendingCompanies ?? []
  const [picked, setPicked] = useState(() => companies.find((company) => company.is_current)?.id ?? '')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<'enter' | 'back' | null>(null)
  const choice = companies.find((company) => company.id === picked && (company.is_current || company.available))

  const enter = async (event: FormEvent) => {
    event.preventDefault()
    if (!choice || busy) return
    setBusy('enter')
    setError(null)
    try {
      await auth.chooseCompany(choice.id)
    } catch (err) {
      const apiError = toApiError(err)
      if (apiError.code === 'COMPANY_NOT_LINKED') setError(t('auth.company.notLinked'))
      else if (apiError.code === 'COMPANY_SWITCH_STALE') setError(t('auth.company.stale'))
      else setError(apiError.message)
      setBusy(null)
    }
  }

  const back = async () => {
    setBusy('back')
    await auth.cancelCompanyChoice()
  }

  return (
    <AuthLayout title={t('auth.company.title')} description={t('auth.company.description')}>
      <form onSubmit={enter} noValidate className="flex flex-col gap-4">
        {error && <InlineAlert tone="danger">{error}</InlineAlert>}
        <div role="radiogroup" aria-label={t('auth.company.title')} className="flex flex-col gap-2">
          {companies.map((company) => {
            const disabled = !company.is_current && !company.available
            const selected = company.id === picked
            return (
              <button
                key={company.id}
                type="button"
                role="radio"
                aria-checked={selected}
                disabled={disabled || busy !== null}
                onClick={() => setPicked(company.id)}
                className={cn(
                  'flex min-h-11 w-full items-center gap-3 rounded-[calc(11px*var(--radius-scale))] border bg-field px-3.5 py-2 text-left transition-colors',
                  selected ? 'border-accent bg-accent-soft' : 'border-field-border hover:border-line-strong',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                )}
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[length:calc(14px*var(--text-scale))] font-medium text-fg">{pickName(company, ui.contentLocale)}</span>
                </span>
                {company.is_current && <span className="shrink-0 text-[length:calc(12px*var(--text-scale))] text-muted">{t('auth.company.lastUsed')}</span>}
                {disabled && <span className="shrink-0 text-[length:calc(12px*var(--text-scale))] text-muted">{t('auth.company.unavailable')}</span>}
              </button>
            )
          })}
        </div>
        <Button
          type="submit"
          variant="primary"
          size="lg"
          loading={busy === 'enter'}
          disabled={!choice || busy !== null}
          className="mt-2 h-11 w-full"
          trailing={busy !== 'enter' && <ArrowRight className="size-4" />}
        >
          {t('auth.company.enter')}
        </Button>
        <Button variant="ghost" loading={busy === 'back'} disabled={busy !== null} onClick={() => void back()} icon={<ArrowLeft className="size-4" />} className="w-full">
          {t('common.actions.back')}
        </Button>
      </form>
    </AuthLayout>
  )
})

export const LoginPage = observer(function LoginPage() {
  const { auth } = useStores()
  const t = useT()
  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [profileId, setProfileId] = useState('')
  const [needsProfile, setNeedsProfile] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!identifier.trim() || !password) {
      setError(t('auth.login.missing'))
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      await auth.login({ identifier: identifier.trim(), password, profile_id: profileId.trim() || undefined })
    } catch (err) {
      const apiError = toApiError(err)
      if (apiError.isConflict && /profile_id/i.test(apiError.message)) {
        // One credential owns several profiles. There is no safe pre-login
        // discovery endpoint yet, so the profile must be supplied explicitly.
        setNeedsProfile(true)
        setError(t('auth.login.multipleProfiles'))
      } else if (apiError.status === 401 || apiError.status === 400) {
        setError(t('auth.login.invalid'))
      } else if (apiError.status === 429) {
        setError(t('auth.login.tooMany'))
      } else {
        setError(apiError.message)
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (auth.pendingCompanies) return <CompanyStep />

  return (
    <AuthLayout
      title={t('auth.login.title')}
      description={t('auth.login.description')}
      footer={
        <>
          {t('auth.login.forgot')}{' '}
          <Link to="/reset-password" className="font-medium text-fg underline-offset-4 hover:underline">
            {t('auth.login.resetBySms')}
          </Link>
        </>
      }
    >
      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        {auth.expiredNotice && !error && (
          <InlineAlert tone="info" action={<button type="button" className="text-[length:calc(12.5px*var(--text-scale))] underline" onClick={() => auth.dismissExpiredNotice()}>{t('common.actions.ok')}</button>}>
            {t('auth.login.expired')}
          </InlineAlert>
        )}
        {error && <InlineAlert tone="danger">{error}</InlineAlert>}
        <Field label={t('auth.login.identifier')} required>
          <Input
            value={identifier}
            onChange={(event) => setIdentifier(event.target.value)}
            autoComplete="username"
            autoFocus
            className="h-11"
            placeholder="+998 90 123 45 67"
          />
        </Field>
        <Field label={t('auth.login.password')} required>
          <PasswordInput value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" />
        </Field>
        {needsProfile && (
          <Field label={t('auth.login.profileId')} required hint={t('auth.login.profileIdHint')}>
            <Input value={profileId} onChange={(event) => setProfileId(event.target.value)} className="num h-10" autoFocus />
          </Field>
        )}
        <Button type="submit" variant="primary" size="lg" loading={submitting} className="mt-2 h-11 w-full" trailing={!submitting && <ArrowRight className="size-4" />}>
          {t('auth.login.submit')}
        </Button>
      </form>
    </AuthLayout>
  )
})
